<script setup lang="ts">
import { vElementVisibility } from "@vueuse/components";
import { groupByKey } from "~/utils/array";
import formatDate from "~/utils/format-date";

export interface BaseListProps<T> {
  limit?: number;
  items?: T[];
  loading?: boolean;
  error?: string | null;
  rootClass?: string;
  loadWhenVisible?: boolean;
  noContentText?: string;
  gridClass?: string;
  title?: string;
  hideOnNoContent?: boolean;
  groupByKey?: keyof T;
  groupByDate?: boolean;
  seeAllLinkPath?: any;
  seeAllLinkPlacement?: "bottom-center" | "top-right";
  hasSeeAllLink?: boolean;
  hasLoadMoreButton?: boolean;
  hasLazyPagination?: boolean;
}

const { t, locale } = useI18n();
const isRootVisible = ref(false);
const props = withDefaults(defineProps<BaseListProps<any>>(), {
  limit: 8,
  gridClass: "main-grid",
});
const emit = defineEmits(["clickLoadMore", "visible"]);
const onLoadMore = () => emit("clickLoadMore");

const isComponentShown = computed(() => {
  if (props.hideOnNoContent) {
    if (props.loadWhenVisible)
      return isRootVisible.value ? props.loading || props.error || props.items?.length : true;
    return props.loading || props.error || props.items?.length;
  }
  return true;
});
function onElementVisibility(isVisible: boolean) {
  isRootVisible.value = isVisible;
  if (!props.loadWhenVisible)
    return;
  const isLoading = props.loading || props.error;
  const hasItems = Boolean(props.items?.length);
  if (isVisible && !isLoading && !hasItems)
    emit("visible");
}
function onEndOfComponentVisible(isVisible: boolean) {
  if (!props.hasLazyPagination)
    return;
  const isLoading = props.loading || props.error;
  const hasItems = Boolean(props.items?.length);
  if (isVisible && !isLoading && hasItems)
    onLoadMore();
}
const getFormattedDate = (date: string): string => formatDate(date, "PP", locale.value as TAvailableLocale);
const listGroupItems = computed(() => props.groupByKey
  ? props.items && groupByKey(props.items, (item) => {
    return props.groupByDate ? getFormattedDate(item[props.groupByKey as string]) : item[props.groupByKey as string];
  })
  : props.items);
</script>

<template>
  <div v-if="isComponentShown" v-element-visibility="onElementVisibility">
    <div v-if="props.title || props.hasSeeAllLink && props.seeAllLinkPlacement === 'top-right'" class="flex items-center justify-between mb-6">
      <div v-if="props.title" class="font-bold">
        {{ props.title }}
      </div>
      <BaseButton
        v-if="props.hasSeeAllLink && props.seeAllLinkPlacement === 'top-right'"
        name="see-all-items"
        class="text-heading-4"
        tag="nuxt-link"
        :to="props.seeAllLinkPath"
        link
      >
        {{ t('labels.see_all') }}
      </BaseButton>
    </div>
    <slot name="start" />

    <template v-if="props.error">
      <slot name="error">
        <BaseNotFound :text="props.error" />
      </slot>
    </template>

    <template v-else-if="props.groupByKey && props.items?.length">
      <template v-for="(groupItems, key) in listGroupItems" :key="key">
        <h3 class="text-heading-3 text-secondary pt-8 px-4 md:px-10 xl:px-12 first:mt-0" :class="[props.gridClass]">
          {{ key }}
        </h3>
        <div :class="[props.gridClass, props.rootClass]">
          <slot v-for="item in groupItems" :key="item" :item="item" name="item" />
        </div>
      </template>
    </template>

    <div v-else-if="props.items?.length || props.loading" :class="[props.gridClass, props.rootClass]">
      <slot v-for="(item, index) in props.items" :key="item" :item="item" :index="index" name="item" />
      <template v-if="props.loading">
        <slot v-for="item in props.limit" :key="item" name="skeleton" />
      </template>
    </div>

    <template v-else>
      <slot name="empty">
        <BaseNotFound :text="props.noContentText" />
      </slot>
    </template>

    <div v-if="props.hasLoadMoreButton" class="mt-10 flex justify-center">
      <BaseButton :disabled="props.loading" name="load-more" link type="primary" class="text-heading-4 mx-8 truncate font-medium" @click="onLoadMore">
        {{ t('labels.load_more') }}
        <IconChevronDown class="inline" :height="8" :width="8" />
      </BaseButton>
    </div>
    <div v-if="props.hasSeeAllLink && props.seeAllLinkPlacement === 'bottom-center'" class="text-center mt-6">
      <BaseButton name="see-all-items" class="text-heading-4" tag="nuxt-link" :to="props.seeAllLinkPath" link>
        {{ t('labels.see_all') }}
      </BaseButton>
    </div>
    <div v-element-visibility="onEndOfComponentVisible" />
    <slot name="end" />
  </div>
</template>
